import { useState, type FormEvent } from 'react'
import { Lock, Eye, EyeOff, ShieldAlert } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import type { useAdminAuth } from '@/hooks/useAdminAuth'

interface AdminLoginProps {
  auth: ReturnType<typeof useAdminAuth>
  onCancel: () => void
}

export function AdminLogin({ auth, onCancel }: AdminLoginProps) {
  const [password, setPassword] = useState('')
  const [show, setShow] = useState(false)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    await auth.login(password)
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] animate-fade-in">
      <div className="w-full max-w-sm">
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-14 h-14 rounded-2xl bg-[var(--accent-blue-light)] flex items-center justify-center text-[var(--accent-blue)]">
            <Lock size={28} />
          </div>
        </div>

        <h1 className="text-xl font-bold text-[var(--text-primary)] text-center mb-1">
          Admin Access
        </h1>
        <p className="text-sm text-[var(--text-secondary)] text-center mb-8">
          Enter the admin password to edit content.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-primary)] mb-1.5">
              Password
            </label>
            <div className="relative">
              <input
                type={show ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoFocus
                autoComplete="current-password"
                className="w-full px-4 py-2.5 pr-10 rounded-lg border border-[var(--border)] bg-[var(--bg-primary)] text-[var(--text-primary)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--accent-blue)] focus:border-transparent transition-all"
                placeholder="Enter password"
              />
              <button
                type="button"
                onClick={() => setShow(s => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
                tabIndex={-1}
              >
                {show ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {auth.error && (
            <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">
              <ShieldAlert size={14} className="flex-shrink-0" />
              {auth.error}
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <Button
              type="button"
              variant="ghost"
              size="md"
              className="flex-1"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="md"
              className="flex-1"
              disabled={!password || auth.isChecking}
            >
              {auth.isChecking ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  Checking…
                </span>
              ) : (
                'Unlock'
              )}
            </Button>
          </div>
        </form>

        <p className="text-xs text-[var(--text-muted)] text-center mt-6">
          Session expires after 4 hours or when the browser tab is closed.
        </p>
      </div>
    </div>
  )
}
